import React from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  Box,
  Tooltip,
  Text,
  Portal,
  Checkbox,
  HStack,
  VStack,
  Button,
  Collapse,
  IconButton,
  Spinner
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

export default function SideBar({ sections, activeSection, onSectionClick, sectionsLoading = false, isCombined = false }) {
  const [hoveredSection, setHoveredSection] = React.useState(null);
  const hoverTimeoutRef = React.useRef(null);
  const enterTimeoutRef = React.useRef(null);
  const lastHoveredSectionIdRef = React.useRef(null);
  
  // Filter state - for normal docs: show only H1 by default, for combined docs: always only H1
  const [showH1, setShowH1] = React.useState(true);
  const [showH2, setShowH2] = React.useState(false); // Default to false for normal docs
  const [showH3, setShowH3] = React.useState(false); // Default to false for normal docs
  const [showFilterPanel, setShowFilterPanel] = React.useState(false);

  const handleMouseEnter = (section) => {
    // Clear any pending timeouts
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // If it's the same section, don't do anything
    if (lastHoveredSectionIdRef.current === section.id) {
      return;
    }
    
    // Add a small delay before showing to prevent rapid switching
    enterTimeoutRef.current = setTimeout(() => {
      lastHoveredSectionIdRef.current = section.id;
      setHoveredSection(section);
    }, 150);
  };

  const handleMouseLeave = () => {
    // Clear enter timeout if mouse leaves before delay
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Add delay before hiding to prevent flickering
    hoverTimeoutRef.current = setTimeout(() => {
      lastHoveredSectionIdRef.current = null;
      setHoveredSection(null);
    }, 200);
  };

  // Filter sections based on heading level
  // For combined docs: always only show H1, ignore filter state
  // For normal docs: use filter state
  const filteredSections = React.useMemo(() => {
    return sections.filter(section => {
      // Ensure level is a number for comparison
      const level = typeof section.level === 'number' 
        ? section.level 
        : (section.level ? parseInt(section.level, 10) : 1);
      
      // Handle invalid/NaN levels by defaulting to 1
      if (isNaN(level) || level < 1 || level > 3) {
        return true; // Show sections with invalid levels by default
      }
      
      // For combined documents, always only show H1
      if (isCombined) {
        return level === 1;
      }
      
      // For normal documents, use filter state
      if (level === 1) return showH1;
      if (level === 2) return showH2;
      if (level === 3) return showH3;
      // Show other levels by default if level is not 1, 2, or 3
      return true;
    });
  }, [sections, showH1, showH2, showH3, isCombined]);

  return (
    <div className="sidebar" style={{ position: 'relative', pointerEvents: 'auto', zIndex: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ 
        position: 'absolute', 
        left: 0, 
        top: 0, 
        width: '220px', 
        height: 'calc(60px + 500px)', // Header height + sections list height (10 sections)
        backgroundColor: '#f3f4f6', 
        zIndex: -1,
        pointerEvents: 'none'
      }}></div>
      <div className="sidebar-header" style={{ position: 'relative', zIndex: 0, flexShrink: 0, paddingTop: '8px', paddingLeft: '8px', paddingRight: '8px' }}>
        <HStack justify="space-between" align="flex-start" mb={2} width="25%" spacing={2}>
          <h2 style={{ margin: 0, flex: 1 }}>Sections ({filteredSections.length}/{sections.length})</h2>
          {/* Only show filter toggle for normal documents, not combined documents */}
          {!isCombined && (
            <IconButton
              size="sm"
              icon={showFilterPanel ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              aria-label="Toggle filters"
              variant="outline"
              colorScheme="blue"
              borderRadius="md"
              flexShrink={0}
              mt={1}
              width="20px"
              height="20px"
              minWidth="20px"
              minHeight="20px"
            />
          )}
        </HStack>
        
        {/* Filter Panel - Only show for normal documents */}
        {!isCombined && (
          <Collapse in={showFilterPanel} animateOpacity>
          <Box
            bg="gray.50"
            p={2}
            borderRadius="md"
            mb={1}
            mt={-1}
            border="1px solid"
            borderColor="gray.200"
          >
            <Text fontSize="xs" fontWeight="bold" mb={1.5} color="gray.600">
              Show Heading Levels:
            </Text>
            <VStack align="start" spacing={1.5}>
              <Checkbox
                isChecked={showH1}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setShowH1(isChecked);
                }}
                size="sm"
              >
                <Text fontSize="xs">H1 (Main Headings)</Text>
              </Checkbox>
              <Checkbox
                isChecked={showH2}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setShowH2(isChecked);
                }}
                size="sm"
              >
                <Text fontSize="xs">H2 (Subheadings)</Text>
              </Checkbox>
              <Checkbox
                isChecked={showH3}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setShowH3(isChecked);
                }}
                size="sm"
              >
                <Text fontSize="xs">H3 (Sub-subheadings)</Text>
              </Checkbox>
              <HStack spacing={2} mt={1.5} width="100%">
                <Button
                  size="xs"
                  colorScheme="blue"
                  onClick={() => {
                    setShowH1(true);
                    setShowH2(true);
                    setShowH3(true);
                  }}
                  flex={1}
                  fontSize="xs"
                  py={1}
                >
                  Show All
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => {
                    setShowH1(false);
                    setShowH2(false);
                    setShowH3(false);
                  }}
                  flex={1}
                  fontSize="xs"
                  py={1}
                >
                  Hide All
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Collapse>
        )}
      </div>

      {/* Info Window - pushes content down */}
      {hoveredSection && (
        <Box
          mx="8px"
          mb={3}
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="md"
          p={3}
          fontSize="xs"
          pointerEvents="auto"
          as="div"
          flexShrink={0}
          onMouseEnter={() => handleMouseEnter(hoveredSection)}
          onMouseLeave={handleMouseLeave}
        >
          <Text 
            fontWeight="bold" 
            color="blue.700" 
            mb={2}
            noOfLines={1}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {hoveredSection.title}
          </Text>
          <Box color="gray.700">
            <Box mb={1}>Created: {hoveredSection.createdAt ? new Date(hoveredSection.createdAt).toLocaleString() : "N/A"}</Box>
            <Box mb={1}>Created By: {hoveredSection.createdBy || "Unknown"}</Box>
            <Box mb={1}>Updated: {hoveredSection.lastupdated ? new Date(hoveredSection.lastupdated).toLocaleString() : "N/A"}</Box>
            <Box>Updated By: {hoveredSection.lastUpdatedBy || "Unknown"}</Box>
          </Box>
        </Box>
      )}

        <div 
          className="sections-scrollable-container"
          style={{ 
            flex: '0 0 auto', // Don't grow, fixed size
            height: '500px', // Fixed height to show ~10 sections (each section ~50px)
            overflowY: 'scroll', // Always show scrollbar
            overflowX: 'hidden',
            paddingRight: '10px',
            paddingLeft: '8px',
            scrollBehavior: 'smooth',
            position: 'relative',
            direction: 'rtl', // Reverse direction to put scrollbar on left
            borderRight: '1px solid #ccc' // Visual indicator for scrollbar area
          }}>
          <div style={{ direction: 'ltr' }}> {/* Reverse content back to normal */}
        <Accordion allowToggle style={{ overflow: 'visible', width: '100%' }}>
          {filteredSections.map((section) => (
            <AccordionItem key={section.id} border="none" style={{ overflow: 'visible', width: '100%' }}>
              <h2 style={{ overflow: 'visible', margin: 0, padding: 0, width: '100%' }}>
                <AccordionButton
                  onClick={() => onSectionClick(section.id)}
                  onMouseEnter={() => handleMouseEnter(section)}
                  onMouseLeave={handleMouseLeave}
                  className={`cursor-pointer p-2 hover:bg-gray-200 border-l-2 border-gray-800
                    ${
                      activeSection === section.id
                        ? "border-l-2 border-blue-400 text-blue-500"
                        : ""
                    }`}
                  style={{ 
                    paddingLeft: `${section.level * 16}px`,
                    overflow: 'visible',
                    width: 'calc(100% + 18px)', // Extend to account for container padding (8px left + 10px right)
                    marginLeft: '-8px', // Extend to left edge
                    marginRight: '-10px', // Extend to right edge
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    paddingRight: '10px' // Maintain right padding for text
                  }}
                >
                  <Box 
                    textAlign="left" 
                    fontSize="sm"
                    style={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      width: 'auto',
                      minWidth: 0,
                      maxWidth: 'none',
                      flex: '0 1 auto',
                      display: 'inline-block'
                    }}
                  >
                    {section.title}
                  </Box>
                  {section.isEditing && (
                    <Text
                      fontSize="xs"
                      color="red.500"
                      ml={2}
                    >
                      Editing...
                    </Text>
                  )}
                </AccordionButton>
              </h2>
            </AccordionItem>
          ))}
        </Accordion>
        {/* Sections loading */}
        {sectionsLoading && sections.length === 0 && (
          <Box
            textAlign="center"
            p={4}
            color="gray.500"
            fontSize="sm"
          >
            <Spinner size="sm" color="blue.500" mr={2} />
            Sections loading...
          </Box>
        )}
        {!sectionsLoading && filteredSections.length === 0 && (
          <Box
            textAlign="center"
            p={4}
            color="gray.500"
            fontSize="sm"
          >
            No sections match the current filter.
            <br />
            <Button
              size="xs"
              mt={2}
              colorScheme="blue"
              variant="link"
              onClick={() => {
                setShowH1(true);
                setShowH2(true);
                setShowH3(true);
              }}
            >
              Show All Sections
            </Button>
          </Box>
        )}
          </div> {/* Close direction: ltr wrapper */}
      </div>
    </div>
  );
}
